import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col } from 'react-bootstrap';
import { FiArrowLeft, FiCheck, FiNavigation, FiFileText } from 'react-icons/fi';
import Breadcrumbs from '../../components/Breadcrumbs';
import Input from '../../components/input';
import Button from '../../components/button';
import LogsComments from '../../components/LogsComments';
import TripCompleteModal from '../../components/modals/TripCompleteModal';
import UserAvatar from '../../components/UserAvatar';
import TripExpenses from './TripExpenses';
import TripPettyCash from './TripPettyCash';
import TripFuelLogs from './TripFuelLogs';
import SpinnerLoader from '../../utils/SpinnerLoader';
import { apiService } from '../../services/apiService';
import { actualTripComplete, fetchTripTimeline, getTripDetails, setSelectedTripId } from '../../store/Slices/TripSlice';
import { fetchCustomerDropdown } from '../../store/Slices/dropdownSlice';
import {
    formatLocalDateTime,
    formatUtcDateTimeForApi,
    formatUtcDateTimeForInput,
    checkTripStagePermission,
    canViewTripRouteJourney,
    cleanProductName,
    hasTripPermission,
    hasTripReachedStage,
    isSuperAdminRole,
} from '../../utils/helper';
import { TripPermission, TripsStageEnum } from '../../utils/app-constant';
import { errorNotify, successNotify } from '../../utils/toast';
import '../../styles/TripInTransit.css';

const getByPath = (source = {}, path = "") =>
    String(path || "")
        .split(".")
        .reduce((current, key) => (current && current[key] !== undefined ? current[key] : undefined), source);

const getFirstValue = (source = {}, paths = [], fallback = "") => {
    for (const path of paths) {
        const value = getByPath(source, path);
        if (value !== undefined && value !== null && value !== "") return value;
    }

    return fallback;
};

const getFirstArrayValue = (source = {}, paths = []) => {
    for (const path of paths) {
        const value = getByPath(source, path);
        if (Array.isArray(value)) return value;
    }

    return [];
};

const getTripCustomerId = (detail = {}) => {
    const customer = detail?.customer;

    return detail?.customer_id ||
        detail?.customerId ||
        detail?.["3pl_customer_id"] ||
        detail?.customer_info?.id ||
        detail?.customer?.id ||
        detail?.customer?.value ||
        (typeof customer === "number" || (typeof customer === "string" && !Number.isNaN(Number(customer))) ? customer : null);
};

const normalizeNumber = (value) => {
    const parsed = Number(String(value ?? "").replace(/,/g, "").trim());
    return Number.isFinite(parsed) ? parsed : null;
};

// covered_distance is 0 until the trip actually moves, so prefer the first non-zero reading
// instead of stopping at that 0 and never reaching total_distance.
const getFirstDistanceValue = (source = {}, paths = []) => {
    for (const path of paths) {
        const parsed = normalizeNumber(getByPath(source, path));
        if (parsed !== null && parsed > 0) return parsed;
    }

    return getFirstValue(source, paths);
};

const formatValue = (value, fallback = "--") => {
    if (value === undefined || value === null || value === "") return fallback;
    return String(value);
};

const formatNumberValue = (value, fallback = "--") => {
    const parsed = normalizeNumber(value);
    if (parsed === null) return fallback;
    return parsed.toLocaleString();
};

const formatCurrencyLabel = (value) => {
    const formatted = formatNumberValue(value);
    return formatted === "--" ? "--" : `PKR ${formatted}`;
};

const formatDistanceValue = (value) => {
    if (value === undefined || value === null || value === "") return "--";
    const parsed = normalizeNumber(value);
    if (parsed === null) return String(value);
    return `${parsed.toLocaleString()} km`;
};

const formatDateTime = (value) => {
    if (!value) return "";

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return String(value);

    return parsed.toLocaleString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const stripHtml = (value) =>
    String(value || "")
        .replace(/<[^>]*>/g, " ")
        .replace(/&nbsp;/g, " ")
        .replace(/\s+/g, " ")
        .trim();

const normalizeStageValue = (value) =>
    String(value || "").trim().toLowerCase().replace(/[\s_]+/g, "-");

const getStopLocationId = (stop = {}) =>
    String(
        getFirstValue(stop, [
            "location_id",
            "customer_warehouse_id",
            "warehouse_id",
            "location.id",
            "location.value",
            "location.customer_warehouse_id",
            "location.warehouse_id",
            "location.warehouse.id",
            "location.warehouse.value",
            "customer_warehouse.id",
            "customer_warehouse.value",
            "customer_warehouse.customer_warehouse_id",
            "customer_warehouse.warehouse_id",
            "customer_warehouse.warehouse.id",
            "customer_warehouse.warehouse.value",
            "warehouse.id",
            "warehouse.value",
        ])
    ).trim();

const getCustomerLocationName = (stop = {}, customerLocations = []) => {
    const stopLocationId = getStopLocationId(stop);
    if (!stopLocationId) return "";

    const matchedLocation = (Array.isArray(customerLocations) ? customerLocations : []).find((location) => {
        const ids = [
            location?.value,
            location?.id,
            location?.customer_warehouse_id,
            location?.warehouse_id,
            location?.raw?.id,
            location?.raw?.value,
            location?.raw?.customer_warehouse_id,
            location?.raw?.warehouse_id,
            location?.raw?.warehouse?.id,
            location?.raw?.warehouse?.value,
        ].map((value) => String(value ?? "").trim()).filter(Boolean);

        return ids.includes(stopLocationId);
    });

    return matchedLocation?.name || matchedLocation?.label || "";
};

const getStopLocationLabel = (stop = {}, customerLocations = []) => {
    const customerLocationName = getCustomerLocationName(stop, customerLocations);
    if (customerLocationName) return customerLocationName;

    const locationName = getFirstValue(stop, [
        "location.warehouse_name",
        "location.name",
        "location.label",
        "location.title",
        "location.location_name",
        "location.point_name",
        "location.warehouse.warehouse_name",
        "location.warehouse.name",
        "location.warehouse.label",
        "location.warehouse.title",
        "customer_warehouse.warehouse_name",
        "customer_warehouse.name",
        "customer_warehouse.label",
        "customer_warehouse.title",
        "customer_warehouse.location_name",
        "customer_warehouse.warehouse.warehouse_name",
        "customer_warehouse.warehouse.name",
        "customer_warehouse.warehouse.label",
        "customer_warehouse.warehouse.title",
        "warehouse.warehouse_name",
        "warehouse.name",
        "warehouse.label",
        "warehouse.title",
        "warehouse_title",
        "warehouse_name",
        "location_name",
        "point_name",
    ]);

    if (locationName) return String(locationName).trim();

    const location = stop?.location;
    if (typeof location === "string" && location.trim()) return location.trim();

    return formatValue(
        getFirstValue(stop, [
            "dispatch_point",
            "decanting_point",
            "start_location",
            "end_location",
            "location_point",
            "location.location_point",
            "location.location",
            "customer_warehouse.address",
            "customer_warehouse.warehouse.address",
            "warehouse.address",
            "address",
        ]),
        "--"
    );
};

const getStopActionLabel = (stop = {}) => {
    const stopType = String(stop?.stop_type || stop?.stopType || "").trim();
    if (!stopType) return "";

    return stopType
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());
};

const getStopDetails = (stop = {}) => {
    const rawProd = stop?.product?.name || stop?.product_name || stop?.product?.label || "";
    const cleanProd = cleanProductName(rawProd);
    const parts = [
        formatDateTime(stop?.eta || stop?.expected_at || stop?.expected_arrival_at || stop?.start_at || stop?.created_at),
        stop?.quantity ? `${formatNumberValue(stop.quantity)} qty` : "",
        cleanProd,
    ].filter(Boolean);

    return parts.join(" - ") || "--";
};

const getTripStops = (detail = {}) => {
    const rawStops = detail?.trip_stops || detail?.stops || [];
    const sortedStops = (Array.isArray(rawStops) ? [...rawStops] : [])
        .sort((a, b) => (Number(a?.stop_number) || 0) - (Number(b?.stop_number) || 0));

    if (sortedStops.length > 0) return sortedStops;

    const startLocation = getFirstValue(detail, [
        "dispatch_point",
        "start_location",
        "startLocation",
        "origin",
        "from_location",
        "fromLocation",
        "loading_point",
        "pickup_location",
        "pickupLocation",
    ]);
    const endLocation = getFirstValue(detail, [
        "decanting_point",
        "end_location",
        "endLocation",
        "destination",
        "to_location",
        "toLocation",
        "unloading_point",
        "dropoff_location",
        "dropoffLocation",
    ]);

    return [
        startLocation ? { id: "detail-start-location", stop_number: 1, stop_type: "load", location: startLocation } : null,
        endLocation ? { id: "detail-end-location", stop_number: 2, stop_type: "unload", location: endLocation } : null,
    ].filter(Boolean);
};

const getVehicleLabel = (detail = {}) => {
    const vehicle = detail?.vehicle || {};
    const registration = vehicle?.registration_number ||
        vehicle?.reg_no ||
        vehicle?.vehicle_number ||
        vehicle?.plate_number ||
        detail?.vehicle_registration_number ||
        detail?.vehicle_number ||
        detail?.vehicle_id;
    const type = detail?.vehicle_type?.name ||
        detail?.vehicle_type_name ||
        vehicle?.vehicle_type?.name ||
        vehicle?.vehicle_type_name ||
        vehicle?.type;

    return [registration || "--", type ? `(${type})` : ""].filter(Boolean).join(" ");
};

const formatPhone = (value) => {
    const phone = String(value || "").trim();
    if (!phone) return "--";
    return phone.startsWith("+") ? phone : `+${phone}`;
};

const sumAmount = (rows = [], paths = ["amount"]) =>
    (Array.isArray(rows) ? rows : []).reduce((sum, row) => {
        const amount = getFirstValue(row, paths, 0);
        return sum + (normalizeNumber(amount) || 0);
    }, 0);

const getAttachment = (item = {}) => {
    const attachments = getFirstArrayValue(item, ["files", "images", "attachments", "documents"]);
    const first = attachments[0];

    if (!first) return null;
    if (typeof first === "string") {
        return {
            name: first.split("/").pop() || "Attachment",
            url: first,
        };
    }

    const url = first?.url || first?.photo_url || first?.file_url || first?.file_path || first?.path || "";
    return {
        name: first?.fileName || first?.file_name || first?.name || first?.title || (url ? url.split("/").pop() : "Attachment"),
        url,
    };
};

const getFuelLogLocation = (log = {}) => {
    const station = log?.fuel_station || log?.station || log?.vendor || {};
    const stationName = station?.fuel_station_name || station?.name || station?.label || station?.title;
    const stationLocation = station?.location || station?.address;

    return [stationName, stationLocation].filter(Boolean).join(", ") ||
        getFirstValue(log, [
            "fuel_station_name",
            "station_name",
            "vendor_name",
            "location",
            "address",
            "source",
        ], "--");
};

const getFuelLogs = (detail = {}) => {
    const rows = getFirstArrayValue(detail, [
        "fuel_logs",
        "fuelLogs",
        "trip_fuel_logs",
        "tripFuelLogs",
        "refueling_history",
        "fuel_records",
        "fuels",
    ]);

    return rows.map((log, index) => {
        const attachment = getAttachment(log);

        return {
            id: log?.id || log?.fuel_log_id || index,
            liters: getFirstValue(log, ["claimed_fueled", "fuel_quantity", "quantity", "litres", "liters", "ltrs", "filled_fuel"]),
            cost: getFirstValue(log, ["total_fuel_cost", "expected_total_fuel_cost", "total_amount", "amount", "cost"]),
            location: getFuelLogLocation(log),
            attachment,
            date: formatDateTime(getFirstValue(log, ["refuel_at", "filled_at", "created_at", "updated_at"])),
        };
    });
};

const getPercentText = (part, total) => {
    const parsedPart = normalizeNumber(part);
    const parsedTotal = normalizeNumber(total);
    if (parsedPart === null || parsedTotal === null || parsedTotal <= 0) return "--";
    return `${Math.round((parsedPart / parsedTotal) * 100)}%`;
};

const parseNumberOrZero = (value) => {
    if (value === "" || value === null || value === undefined) return 0;
    const numericValue = Number(value);
    return Number.isNaN(numericValue) ? 0 : numericValue;
};

const isEndBeforeStart = (startValue, endValue) => {
    if (!startValue || !endValue) return false;

    const start = new Date(startValue);
    const end = new Date(endValue);

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return false;

    return end.getTime() < start.getTime();
};

const isEndOdometerNotGreaterThanStart = (startValue, endValue) => {
    if (endValue === "" || endValue === null || endValue === undefined) return false;
    if (startValue === "" || startValue === null || startValue === undefined) return false;

    const start = normalizeNumber(startValue);
    const end = normalizeNumber(endValue);

    if (start === null || end === null) return false;

    return end <= start;
};

const TripInTransit = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const dispatch = useDispatch();

    const realTripId = useMemo(() => {
        if (!id) return null;
        return String(id).replace(/^TR-/i, "");
    }, [id]);

    const { tripDetailsData, tripDetailsLoading } = useSelector((state) => state.trips);
    const { customerDropdownData } = useSelector((state) => state.dropdowns || state.dropdown || {});
    const detailData = useMemo(() => tripDetailsData?.data || tripDetailsData || {}, [tripDetailsData]);

    const [tripDetailsRequestPending, setTripDetailsRequestPending] = useState(() => Boolean(realTripId));
    const [loadedTripDetailsId, setLoadedTripDetailsId] = useState(null);
    const [fetchedCustomerName, setFetchedCustomerName] = useState("");
    const [customerLocations, setCustomerLocations] = useState([]);
    const [tripCompleteModalOpen, setTripCompleteModalOpen] = useState(false);
    const [tripCompleteLoading, setTripCompleteLoading] = useState(false);
    const [fuelSheetLoading, setFuelSheetLoading] = useState(false);
    const [builtyLoading, setBuiltyLoading] = useState(false);
    const [podLoading, setPodLoading] = useState(false);

    const canCompleteTrip = useMemo(() => isSuperAdminRole() || hasTripPermission(TripPermission.Completed), []);

    // Once the trip has already moved past In Transit (e.g. Unverified/Verified/Completed),
    // this stage's data is locked - still viewable via Back, but Mark Completed must stay disabled.
    const isTripPastInTransit = useMemo(
        () => hasTripReachedStage(detailData?.stage, TripsStageEnum.Unverified),
        [detailData?.stage]
    );

    const isTripCompleted = useMemo(
        () => hasTripReachedStage(detailData?.stage, TripsStageEnum.Completed),
        [detailData?.stage]
    );

    const handleBackToReadyDispatch = () => {
        const previousStageCheck = checkTripStagePermission("checklists-and-allocations");
        if (previousStageCheck.allowed) {
            navigate(`/trips-list/ready-dispatch/${dispatchTripId}`);
        } else {
            errorNotify("You do not have permission to access Ready to Dispatch");
        }
    };
    const canViewFuelSheet = useMemo(() => isSuperAdminRole() || hasTripPermission([TripPermission.FuelSheet, 'fuel-sheet', 'Fuel Sheet', 'fuel_sheet']), []);
    const canViewBuilty = useMemo(() => isSuperAdminRole() || hasTripPermission([TripPermission.Builty, 'Builty', 'builty', 'bilty', 'Bilty', 'trip-builty']), []);
    const canViewPod = useMemo(() => isSuperAdminRole() || hasTripPermission([TripPermission.POD, 'POD', 'pod', 'Pod', 'trip-pod', 'Trip-POD']), []);
    const canViewFuelLogs = useMemo(() => isSuperAdminRole() || hasTripPermission(TripPermission.FuelLogsView), []);
    const canAddFuelLogs = useMemo(() => isSuperAdminRole() || hasTripPermission(TripPermission.FuelLogsAdd), []);
    const canEditFuelLogs = useMemo(() => isSuperAdminRole() || hasTripPermission(TripPermission.FuelLogsEdit), []);
    const canDeleteFuelLogs = useMemo(() => isSuperAdminRole() || hasTripPermission(TripPermission.FuelLogsDelete), []);
    const canViewPettyCash = useMemo(() => isSuperAdminRole() || hasTripPermission(TripPermission.PettyCashView), []);
    const canAddPettyCash = useMemo(() => isSuperAdminRole() || hasTripPermission(TripPermission.PettyCashAdd), []);
    const canEditPettyCash = useMemo(() => isSuperAdminRole() || hasTripPermission(TripPermission.PettyCashEdit), []);
    const canDeletePettyCash = useMemo(() => isSuperAdminRole() || hasTripPermission(TripPermission.PettyCashDelete), []);
    const canViewTripExpenses = useMemo(() => isSuperAdminRole() || hasTripPermission(TripPermission.ExpensesView), []);
    const canAddTripExpenses = useMemo(() => isSuperAdminRole() || hasTripPermission(TripPermission.ExpensesAdd), []);
    const canEditTripExpenses = useMemo(() => isSuperAdminRole() || hasTripPermission(TripPermission.ExpensesEdit), []);
    const canDeleteTripExpenses = useMemo(() => isSuperAdminRole() || hasTripPermission(TripPermission.ExpensesDelete), []);
    const canViewRouteJourney = useMemo(() => canViewTripRouteJourney(), []);
    const isCorrectionMode = normalizeStageValue(detailData?.stage) === TripPermission.Correction;
    const screenTitle = isCorrectionMode ? "Correction" : "In Transit";

    const handleOpenFuelSheet = async () => {
        const tripNumericId = detailData?.id || realTripId;
        if (!tripNumericId) {
            errorNotify("Trip ID not found");
            return;
        }

        setFuelSheetLoading(true);
        try {
            const response = await apiService.getFuelSheet(tripNumericId);
            const fileUrl = response?.data?.url || response?.url;
            if (fileUrl) {
                window.open(fileUrl, "_blank");
            } else {
                errorNotify("Fuel sheet URL not found");
            }
        } catch (error) {
            console.error("Error generating fuel sheet:", error);
            errorNotify(typeof error === "string" ? error : error?.message || "Failed to generate fuel sheet");
        } finally {
            setFuelSheetLoading(false);
        }
    };

    const handleOpenBuilty = async () => {
        const tripNumericId = detailData?.id || realTripId;
        if (!tripNumericId) {
            errorNotify("Trip ID not found");
            return;
        }

        setBuiltyLoading(true);
        try {
            const response = await apiService.getBilty(tripNumericId);
            const fileUrl = response?.data?.url || response?.url;
            if (fileUrl) {
                window.open(fileUrl, "_blank");
            } else {
                errorNotify("Builty URL not found");
            }
        } catch (error) {
            console.error("Error generating builty:", error);
            errorNotify(typeof error === "string" ? error : error?.message || "Failed to generate builty");
        } finally {
            setBuiltyLoading(false);
        }
    };

    const handleOpenPod = async () => {
        const tripNumericId = detailData?.id || realTripId;
        if (!tripNumericId) {
            errorNotify("Trip ID not found");
            return;
        }

        setPodLoading(true);
        try {
            const response = await apiService.getPod(tripNumericId);
            const fileUrl = response?.data?.url || response?.url || (typeof response === "string" ? response : null);
            if (fileUrl) {
                window.open(fileUrl, "_blank");
            } else {
                errorNotify("POD URL not found");
            }
        } catch (error) {
            console.error("Error generating POD:", error);
            errorNotify(typeof error === "string" ? error : error?.message || "Failed to generate POD");
        } finally {
            setPodLoading(false);
        }
    };

    useEffect(() => {
        let isActive = true;

        if (!realTripId) {
            setTripDetailsRequestPending(false);
            setLoadedTripDetailsId(null);
            return () => {
                isActive = false;
            };
        }

        setTripDetailsRequestPending(true);
        dispatch(getTripDetails(realTripId)).finally(() => {
            if (isActive) {
                setLoadedTripDetailsId(realTripId);
                setTripDetailsRequestPending(false);
            }
        });
        dispatch(fetchTripTimeline(realTripId));
        dispatch(setSelectedTripId(realTripId));
        dispatch(fetchCustomerDropdown());

        return () => {
            isActive = false;
        };
    }, [dispatch, realTripId]);

    useEffect(() => {
        let isActive = true;

        const fetchCustomerDetailData = async () => {
            const custId = getTripCustomerId(detailData);
            setFetchedCustomerName("");
            setCustomerLocations([]);

            if (!custId) return;

            try {
                const response = await apiService.getCustomerTripDetail(custId);
                const data = response?.data || response || {};
                const customer = detailData?.customer;
                const cName = data?.name ||
                    data?.customer_name ||
                    data?.company_name ||
                    data?.title ||
                    (typeof customer === "object" ? customer?.name : "") ||
                    (typeof customer === "string" && Number.isNaN(Number(customer)) ? customer : "");

                if (isActive && cName) {
                    setFetchedCustomerName(cName);
                }

                const locations = data?.locations ||
                    data?.warehouses ||
                    data?.customer_locations ||
                    data?.customer_warehouses ||
                    [];
                const formattedLocations = (Array.isArray(locations) ? locations : []).map((locationItem) => {
                    const name = getFirstValue(locationItem, [
                        "warehouse_name",
                        "name",
                        "label",
                        "title",
                        "location_name",
                        "point_name",
                        "warehouse.warehouse_name",
                        "warehouse.name",
                        "warehouse.label",
                    ]);

                    return {
                        value: String(locationItem?.id || locationItem?.value || locationItem?.customer_warehouse_id || locationItem?.warehouse_id || locationItem?.warehouse?.id || ""),
                        id: locationItem?.id,
                        customer_warehouse_id: locationItem?.customer_warehouse_id,
                        warehouse_id: locationItem?.warehouse_id || locationItem?.warehouse?.id,
                        name: name || "",
                        label: name || "",
                        raw: locationItem,
                    };
                });

                if (isActive) {
                    setCustomerLocations(formattedLocations);
                }
            } catch (err) {
                console.error("Error fetching customer trip detail:", err);
            }
        };

        fetchCustomerDetailData();

        return () => {
            isActive = false;
        };
    }, [detailData]);

    const tripIdDisplay = detailData?.trip_id || id || "--";
    const dispatchTripId = realTripId || detailData?.id || id;

    const customerName = useMemo(() => {
        const custObj = detailData?.customer;
        if (typeof custObj === "object" && custObj !== null) {
            const nameStr = custObj.name || custObj.label || custObj.title || custObj.customer_name || custObj.company_name;
            if (nameStr) return nameStr;
        }

        const custId = getTripCustomerId(detailData);
        if (Array.isArray(customerDropdownData) && customerDropdownData.length > 0 && custId) {
            const foundCust = customerDropdownData.find((customer) => String(customer.value || customer.id) === String(custId));
            if (foundCust) return foundCust.label || foundCust.name || foundCust.title;
        }

        return fetchedCustomerName ||
            (typeof detailData?.customer_name === "string" ? detailData.customer_name : "") ||
            (typeof custObj === "string" && Number.isNaN(Number(custObj)) ? custObj : "") ||
            "--";
    }, [detailData, customerDropdownData, fetchedCustomerName]);

    const vehicleLabel = useMemo(() => getVehicleLabel(detailData), [detailData]);
    const driverName = detailData?.primary_driver_name ||
        detailData?.primary_driver?.name ||
        detailData?.primary_driver?.username ||
        detailData?.driver_name ||
        "--";
    const driverPhone = formatPhone(detailData?.primary_driver_contact || detailData?.primary_driver?.phone || detailData?.driver_contact);
    const driverImage = detailData?.primary_driver?.image || detailData?.primary_driver?.avatar || null;

    const routeStops = useMemo(() => getTripStops(detailData), [detailData]);
    const routeStopCount = routeStops.length;
    const fuelLogs = useMemo(() => getFuelLogs(detailData), [detailData]);
    const tripPettyCash = Array.isArray(detailData?.trip_petty_cash) ? detailData.trip_petty_cash : [];
    const tripExpenses = Array.isArray(detailData?.trip_expenses) ? detailData.trip_expenses : [];

    const allocatedPettyCash = getFirstValue(detailData, ["allocated_petty_cash", "allocatedPettyCash"]);
    const totalPettyCashAmount = sumAmount(tripPettyCash);
    const totalPettyCashDisplayValue = normalizeNumber(allocatedPettyCash) !== null ? allocatedPettyCash : totalPettyCashAmount;
    const totalTripExpenseAmount = sumAmount(tripExpenses);
    const remainingPettyCashAmount = Math.max((normalizeNumber(totalPettyCashDisplayValue) || 0) - totalTripExpenseAmount, 0);
    const fuelCost = getFirstValue(detailData, ["fuelCost", "fuel_cost", "total_fuel_cost", "totalTripFuelCost"], sumAmount(fuelLogs, ["cost", "total_fuel_cost", "total_amount", "amount"]));
    const totalFilledFuel = getFirstValue(detailData, ["total_filled_fuel", "filled_fuel", "filledFuel"]);
    const openingFuel = getFirstValue(detailData, ["opening_fuel", "openingFuel"]);
    const allocatedFuel = getFirstValue(detailData, ["allocated_fuel", "allocatedFuel"]);
    const startOdometer = getFirstValue(detailData, ["start_odm", "start_odometer", "startOdometer"]);
    const distanceValue = getFirstDistanceValue(detailData, ["covered_distance", "total_distance", "distance", "trip_distance", "route_distance", "distance_km"]);
    const departedAt = getFirstValue(detailData, ["actual_start_at", "started_at", "dispatch_at", "start_at", "created_at"]);
    const notes = getFirstValue(detailData, ["general_notes", "in_transit_notes", "loading_dispatch_notes", "dispatch_notes", "notes", "note"]);
    const currentSpeed = getFirstValue(detailData, ["trip_current_detail.speed", "current_speed", "speed"]);

    const completionInitialValues = useMemo(() => {
        const rawStops = detailData?.trip_stops || detailData?.stops || [];
        const stopsArray = Array.isArray(rawStops) ? rawStops : [];
        const totalQty = Number(
            detailData?.total_load ??
            detailData?.total_quantity ??
            detailData?.trip_load_sum?.total_load ??
            detailData?.quantity ??
            (stopsArray.length > 0
                ? stopsArray.filter((s) => s.stop_type === "load").reduce((sum, s) => sum + (Number(s.quantity) || 0), 0) ||
                  stopsArray.reduce((sum, s) => sum + (Number(s.quantity) || 0), 0)
                : 0)
        ) || 0;

        return {
            startOdometer: startOdometer || "",
            endOdometer: detailData?.end_odm ?? "",
            closing_fuel: detailData?.closing_fuel ?? "",
            shortage_qty: "",
            relief_qty: "",
            delivered_qty: "",
            totalQuantity: totalQty,
            trip_stops: stopsArray,
            startDateTime: formatUtcDateTimeForInput(detailData?.actual_start_at || detailData?.started_at || detailData?.start_at),
            tripId: detailData?.trip_id || dispatchTripId,
            vehicleReg: detailData?.vehicle?.registration_number || detailData?.registration_number || (typeof detailData?.vehicle === 'string' ? detailData.vehicle : ""),
            customerName: detailData?.customer?.name || detailData?.customer_name || (typeof detailData?.customer === 'string' ? detailData.customer : ""),
        };
    }, [detailData, startOdometer, dispatchTripId]);

    const isPageLoading = Boolean(
        realTripId && (tripDetailsLoading || tripDetailsRequestPending || loadedTripDetailsId !== realTripId)
    );

    const canManageTripExpenses = totalPettyCashAmount > 0 && remainingPettyCashAmount > 0;
    const tripExpensesLockReason = totalPettyCashAmount <= 0
        ? "Trip Expenses can only be added when petty cash exists."
        : remainingPettyCashAmount <= 0
            ? "Trip Expenses limit reached. Petty cash amount has been fully used."
            : "";

    const openCompleteModal = () => {
        if (!canCompleteTrip) {
            errorNotify("You do not have permission to mark this trip completed");
            return;
        }

        if (isTripPastInTransit) {
            errorNotify("This trip has already moved past In Transit and can no longer be edited");
            return;
        }

        setTripCompleteModalOpen(true);
    };

    const closeCompleteModal = () => {
        if (tripCompleteLoading) return;
        setTripCompleteModalOpen(false);
    };

    const handleTripCompleteConfirm = async (values) => {
        if (!dispatchTripId) return;

        if (isEndOdometerNotGreaterThanStart(completionInitialValues?.startOdometer, values?.end_odm)) {
            errorNotify("End Odometer Reading must be greater than Start Odometer");
            return;
        }

        if (isEndBeforeStart(completionInitialValues?.startDateTime, values?.actual_end_at)) {
            errorNotify(`Actual end date & time must be after ${formatLocalDateTime(completionInitialValues?.startDateTime)}.`);
            return;
        }

        const actualEndAt = formatUtcDateTimeForApi(values.actual_end_at);

        if (!actualEndAt) {
            errorNotify("Actual End Date & Time is required.");
            return;
        }

        setTripCompleteLoading(true);

        try {
            await dispatch(actualTripComplete({
                trip_id: Number(dispatchTripId) || dispatchTripId,
                end_odm: parseNumberOrZero(values.end_odm),
                closing_fuel: parseNumberOrZero(values.closing_fuel),
                shortage_qty: parseNumberOrZero(values.shortage_qty),
                relief_qty: parseNumberOrZero(values.relief_qty),
                delivered_qty: parseNumberOrZero(values.delivered_qty),
                actual_end_at: actualEndAt,
            })).unwrap();

            setTripCompleteModalOpen(false);
            successNotify("Trip completed successfully.");
            const nextStageCheck = checkTripStagePermission("completed");
            if (nextStageCheck.allowed) {
                navigate(`/trips-list/trip-completed/${dispatchTripId}`, { replace: true });
            } else {
                navigate("/trips-list", { replace: true });
            }
        } catch (error) {
            errorNotify(error);
        } finally {
            setTripCompleteLoading(false);
        }
    };

    if (isPageLoading) {
        return <SpinnerLoader />;
    }

    return (
        <div className="trip_in_transit_page">
            <div className="desktop_header_only">
                <Breadcrumbs values={["Trip Management", "Trip Detail", screenTitle]} />
                <div className="access_view transit_top_bar">
                    <h2>{screenTitle}</h2>
                    <div className="transit_top_actions">
                        {canViewBuilty && (
                            <button
                                type="button"
                                className="btn_builty"
                                onClick={handleOpenBuilty}
                                disabled={builtyLoading}
                                title="View Builty PDF"
                            >
                                {builtyLoading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: '12px', height: '12px', borderWidth: '2px' }} />
                                        <span>Loading...</span>
                                    </>
                                ) : (
                                    <>
                                        <FiFileText style={{ fontSize: '14px' }} />
                                        <span>Builty</span>
                                    </>
                                )}
                            </button>
                        )}
                        {canViewPod && (
                            <button
                                type="button"
                                className="btn_builty"
                                onClick={handleOpenPod}
                                disabled={podLoading}
                                title="View POD PDF"
                            >
                                {podLoading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: '12px', height: '12px', borderWidth: '2px' }} />
                                        <span>Loading...</span>
                                    </>
                                ) : (
                                    <>
                                        <FiFileText style={{ fontSize: '14px' }} />
                                        <span>POD</span>
                                    </>
                                )}
                            </button>
                        )}
                        <button
                            className="btn_mark_completed_header"
                            type="button"
                            onClick={openCompleteModal}
                            disabled={tripCompleteLoading || !canCompleteTrip || isTripPastInTransit}
                        >
                            Mark Trip Completed
                        </button>
                        <Button
                            cancelBtn={handleBackToReadyDispatch}
                            cancelBtnText="Back"
                            shouldRow={false}
                            isShowSubmitBtn={false}
                        />
                    </div>
                </div>
            </div>

            <div className="mobile_header_only mobile_transit_header">
                <div className="mobile_header_left_controls">
                    <button className="mobile_menu_icon_btn" onClick={handleBackToReadyDispatch} type="button" title="Back">
                        <FiArrowLeft style={{ fontSize: '20px' }} />
                    </button>
                    <h1 className="mobile_header_title">{screenTitle}</h1>
                </div>
            </div>

            <Row className="transit_main_row g-3">
                <Col lg={8} md={12} className="transit_left_col">
                    <div className="transit_card main_transit_card">
                        <div className="transit_card_header">
                            <div>
                                <h3 className="title">Trip In Progress</h3>
                                <p className="subtitle">Keep track of the vehicle's progress until the trip is complete.</p>
                            </div>
                            <div className="trip_id_purple_pill">
                                Trip ID: {tripIdDisplay}
                            </div>
                        </div>

                        <div className="transit_meta_info_card">
                            <div className="meta_info_item">
                                <span className="meta_info_label">Customer</span>
                                <span className="meta_info_value">{customerName}</span>
                            </div>
                            <div className="meta_info_item">
                                <span className="meta_info_label">Vehicle</span>
                                <span className="meta_info_value">
                                    <a href="#vehicle" className="meta_info_value_link">{vehicleLabel}</a>
                                </span>
                            </div>
                            <div className="meta_info_item">
                                <span className="meta_info_label">Driver</span>
                                <div className="driver_meta_row">
                                    <UserAvatar username={driverName} image={driverImage} size={28} />
                                    <span className="driver_name_link">{driverName}</span>
                                </div>
                            </div>
                            <div className="meta_info_item">
                                <span className="meta_info_label">Driver Contact</span>
                                <span className="meta_info_value">{driverPhone}</span>
                            </div>
                            <div className="meta_info_item view_live_col">
                                <div className="view_live_trip_wrapper">
                                    <div className="view_live_outer_ring">
                                        <div className="view_live_inner_ring">
                                            <button
                                                className="btn_view_live_trip_blue"
                                                type="button"
                                                onClick={() => successNotify("Opening Live Trip Map...")}
                                            >
                                                <FiNavigation className="nav_icon_white" /> View Live Trip
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="green_in_transit_banner">
                            <span className="status_dot"></span>
                            <span className="status_text">
                                {departedAt ? `In transit - departed ${formatDateTime(departedAt)}` : "In transit"}
                            </span>
                        </div>

                        <div className="transit_stats_grid">
                            <div className="transit_stat_item">
                                <span className="stat_label">Total Petty Cash</span>
                                <div className="stat_val_row">
                                    <span className="bar_green"></span>
                                    <span className="stat_num">{formatNumberValue(totalPettyCashDisplayValue)}</span>
                                </div>
                                <span className="stat_sub">Total Amount in PKR</span>
                            </div>

                            <div className="transit_stat_item">
                                <span className="stat_label">Petty Cash Balance</span>
                                <div className="stat_val_row">
                                    <span className="bar_red"></span>
                                    <span className="stat_num">{formatNumberValue(remainingPettyCashAmount)}</span>
                                </div>
                                <span className="stat_sub"><strong style={{ color: '#2563eb' }}>{getPercentText(totalTripExpenseAmount, totalPettyCashDisplayValue)}</strong> used from total amount</span>
                            </div>

                            <div className="transit_stat_item">
                                <span className="stat_label">Total Fuel Cost</span>
                                <div className="stat_val_row">
                                    <span className="bar_amber"></span>
                                    <span className="stat_num">{formatNumberValue(fuelCost)}</span>
                                </div>
                                <span className="stat_sub">Total Amount in PKR</span>
                            </div>

                            <div className="transit_stat_item">
                                <span className="stat_label">Total Expense</span>
                                <div className="stat_val_row">
                                    <span className="bar_blue"></span>
                                    <span className="stat_num">{formatNumberValue(totalTripExpenseAmount)}</span>
                                </div>
                                <span className="stat_sub"><strong style={{ color: '#2563eb' }}>{getPercentText(totalTripExpenseAmount, totalPettyCashDisplayValue)}</strong> of petty cash</span>
                            </div>
                        </div>

                        <div className="transit_ops_grid">
                            <div className="ops_item">
                                <span className="ops_label">Opening Fuel</span>
                                <span className="ops_value">{formatNumberValue(openingFuel)} Ltrs</span>
                            </div>
                            <div className="ops_item">
                                <span className="ops_label">Allocated Fuel</span>
                                <span className="ops_value">{formatNumberValue(allocatedFuel)} Ltrs</span>
                            </div>
                            <div className="ops_item">
                                <span className="ops_label">Start Odometer</span>
                                <span className="ops_value">{formatNumberValue(startOdometer)} km</span>
                            </div>
                            <div className="ops_item">
                                <span className="ops_label">Current Speed</span>
                                <span className="ops_value">{normalizeNumber(currentSpeed) !== null ? `${formatNumberValue(currentSpeed)} km/h` : "--"}</span>
                            </div>
                        </div>

                        {canViewRouteJourney && <div className="route_journey_block mt-4">
                            <div className="route_journey_header">
                                <h4 className="journey_title">Route Journey</h4>
                                <span className="journey_sub_info">{formatDistanceValue(distanceValue)} · {routeStopCount} {routeStopCount === 1 ? 'stop' : 'stops'}</span>
                            </div>

                            <div className="route_journey_timeline">
                                {routeStops.length > 0 ? (
                                    routeStops.map((stop, index) => {
                                        const isFirst = index === 0;
                                        const isLast = index === routeStops.length - 1;
                                        const badgeLabel = isFirst ? "Trip Start" : isLast ? "Trip End" : `Stop-${index}`;
                                        const actionLabel = getStopActionLabel(stop);
                                        const nodeClass = isFirst ? "node_start" : isLast ? "node_end" : "node_stop";

                                        return (
                                            <div className="timeline_step_item" key={stop?.id || `${index}-${getStopLocationLabel(stop, customerLocations)}`}>
                                                <div className={`timeline_node ${nodeClass}`}>
                                                    <span>{isFirst ? <FiCheck /> : (isLast ? (isTripCompleted ? <FiCheck /> : (index + 1)) : index)}</span>
                                                </div>
                                                <div className="timeline_content">
                                                    <div className="timeline_badges">
                                                        <span className={isFirst ? "badge_green_light" : isLast ? "badge_purple_light" : "badge_red_light"}>{badgeLabel}</span>
                                                        {actionLabel && <span className="action_text">{actionLabel}</span>}
                                                    </div>
                                                    <h5 className="stop_location_name">{getStopLocationLabel(stop, customerLocations)}</h5>
                                                    <p className="stop_meta_info">{getStopDetails(stop)}</p>
                                                </div>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className="transit_empty_state">No route stops found.</div>
                                )}
                            </div>
                        </div>}
                        {canViewFuelLogs && (
                        <div className="log_section_block mt-4">
                            <div className="log_section_header">
                                <h4 className="log_title">Fuel Log</h4>
                                <div className="log_section_header_right">
                                    {canViewFuelSheet && (
                                        <button
                                            type="button"
                                            className="btn_fuel_sheet"
                                            onClick={handleOpenFuelSheet}
                                            disabled={fuelSheetLoading}
                                            title="View Fuel Sheet PDF"
                                        >
                                            {fuelSheetLoading ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: '12px', height: '12px', borderWidth: '2px' }} />
                                                    <span>Loading...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <FiFileText style={{ fontSize: '14px' }} />
                                                    <span>Fuel Sheet</span>
                                                </>
                                            )}
                                        </button>
                                    )}
                                    {totalFilledFuel ? <span className="rate_pill">{formatNumberValue(totalFilledFuel)} L filled</span> : null}
                                </div>
                            </div>
                            <TripFuelLogs
                                tripFuelLogs={detailData?.trip_fuel_logs || detailData?.fuel_logs || []}
                                trip_id={realTripId}
                                tripIdDisplay={tripIdDisplay}
                                vehicle_id={detailData?.vehicle_id || detailData?.vehicle?.id}
                                vehicle_label={vehicleLabel}
                                fuel_type={detailData?.vehicle?.fuel_type || detailData?.vehicle?.fuelType || detailData?.fuel_type || 'diesel'}
                                trip_start_at={departedAt || detailData?.start_at}
                                isTripCompleted={isTripCompleted}
                                canAddFuelLog={canAddFuelLogs}
                                canEditFuelLog={canEditFuelLogs}
                                canDeleteFuelLog={canDeleteFuelLogs}
                                allocated_fuel={allocatedFuel}
                                total_filled_fuel={totalFilledFuel}
                            />
                        </div>
                        )}

                        {canViewPettyCash && (
                        <div className="log_section_block mt-4">
                            <div className="log_section_header">
                                <h4 className="log_title">Petty Cash Detail</h4>
                            </div>
                            <TripPettyCash
                                tripPettyCash={tripPettyCash}
                                vehicle_id={detailData?.vehicle_id || detailData?.vehicle?.id}
                                trip_start_at={departedAt || detailData?.start_at}
                                isTripCompleted={false}
                                disablePettyCashActions={false}
                                allocatedPettyCashAmount={normalizeNumber(allocatedPettyCash) || 0}
                                totalPettyCashAmount={totalPettyCashAmount}
                                canAddPettyCash={canAddPettyCash}
                                canEditPettyCash={canEditPettyCash}
                                canDeletePettyCash={canDeletePettyCash}
                            />
                        </div>
                        )}

                        {canViewTripExpenses && (
                        <div className="log_section_block mt-4">
                            <div className="log_section_header">
                                <h4 className="log_title">Trip Expenses</h4>
                            </div>
                            <TripExpenses
                                tripExpenses={tripExpenses}
                                vehicle_id={detailData?.vehicle_id || detailData?.vehicle?.id}
                                driver_id={detailData?.primary_driver_id || detailData?.primary_driver?.id}
                                isTripCompleted={false}
                                totalPettyCashAmount={totalPettyCashAmount}
                                totalTripExpenseAmount={totalTripExpenseAmount}
                                remainingPettyCashAmount={remainingPettyCashAmount}
                                canManageTripExpenses={canManageTripExpenses && (canAddTripExpenses || canEditTripExpenses || canDeleteTripExpenses)}
                                tripExpensesLockReason={tripExpensesLockReason}
                                canAddTripExpense={canAddTripExpenses}
                                canEditTripExpense={canEditTripExpenses}
                                canDeleteTripExpense={canDeleteTripExpenses}
                            />
                        </div>
                        )}
                    </div>
                </Col>

                <Col lg={4} md={12} className="transit_right_col">
                    <div className="driver_details_sidebar service_cost_summary_details">
                        <LogsComments />
                    </div>

                    <div className="trip_notes_main_div mt-3">
                        <h5>General Notes</h5>
                        <div style={{ padding: '0 16px 16px 16px' }}>
                            <Input
                                label=""
                                name="general_notes"
                                placeholder="No notes added."
                                quill={true}
                                value={notes}
                                disabled={true}
                                required={false}
                            />
                        </div>
                    </div>


                    <div className="mobile_only_block mt-3">
                        {canCompleteTrip && (
                            <button className="btn_full_mark_completed" type="button" onClick={openCompleteModal} disabled={tripCompleteLoading || isTripPastInTransit}>
                                Mark Trip Completed
                            </button>
                        )}
                    </div>
                </Col>
            </Row>

            <TripCompleteModal
                open={tripCompleteModalOpen}
                onClose={closeCompleteModal}
                onConfirm={handleTripCompleteConfirm}
                loading={tripCompleteLoading}
                initialValues={completionInitialValues}
                centered={true}
            />
        </div>
    );
};

export default TripInTransit;
